import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Segmented, Sheet, Spinner } from '@/components/common';
import { useResource, useStoredState } from '@/hooks';
import { LANGUAGES } from '@/i18n';
import { errorMessage } from '@/lib/api-client';
import { navigate } from '@/lib/router';
import type { Entry, EntryKind, Transcript, Visit, Xray } from '@/types/api';
import type { ToothState } from '@/utils/toothState';
import type { VisitStep } from '@/utils/visitHistory';
import { useAuth } from '../auth';
import { type Clarification, DictationPanel } from '../dictation';
import { useDoctor } from '../doctors';
import { HOME_TAB_KEY, type HomeTab, HomeTabs } from '../home';
import { patientsApi } from '../patients/api';
import { visitsApi } from './api';
import {
  ChartFab,
  ChartLegend,
  type Dentition,
  EntryForm,
  type EntryValues,
  HistoryBanner,
  isToday,
  Odontogram,
  PatientHeader,
  PatientInfo,
  type SpokenPrice,
  ToothSheet,
  TranscriptCard,
  VisitCard,
  VisitStepper,
  XrayCard,
} from './components';
import { useChartDictation } from './hooks/useChartDictation';
import { useChartEntries } from './hooks/useChartEntries';
import { useChartTranscripts } from './hooks/useChartTranscripts';
import { useChartView } from './hooks/useChartView';
import { useChartXrays } from './hooks/useChartXrays';
import { useRecordingControls } from './hooks/useRecordingControls';
import { useVisitHistory } from './hooks/useVisitHistory';
import { useVisitSignOff } from './hooks/useVisitSignOff';

const NO_TEETH: ReadonlySet<number> = new Set();

/**
 * The patient chart in three columns: patient info, the teeth (as large as
 * possible), and the visit (dictation, entries, price and change log).
 */
export function ChartPage({ patientId }: { patientId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { doctorId } = useDoctor();
  const [, setHomeTab] = useStoredState<HomeTab>(HOME_TAB_KEY, 'bookings');
  const patient = useResource(() => patientsApi.get(patientId), patientId);
  const data = useChartEntries(patientId, user, doctorId);
  const dictations = useChartTranscripts(patientId, user, doctorId);
  const xrays = useChartXrays(patientId, user, doctorId);
  const view = useChartView(data.entries, patient.data?.visits);
  // A patient's first visit records what is found; later ones what is done.
  const mode: EntryKind = view.hasHistory ? 'PERFORMED' : 'EXISTING';
  // "3000 dinara" while dictating fills the visit's price field; the doctor confirms it.
  const [spokenPrice, setSpokenPrice] = useState<SpokenPrice | null>(null);
  const dictation = useChartDictation(data, dictations.record, mode, (price) =>
    setSpokenPrice({ price })
  );
  const { speech } = dictation;

  const [selected, setSelected] = useState<number | null>(null);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [manual, setManual] = useState<ManualEntry | null>(null);
  const [dentitionChoice, setDentition] = useState<Dentition | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Looking back stops recording.
  const recording = useRecordingControls(speech);
  const { typing, setTyping, stopAll } = recording;
  const history = useVisitHistory(patient.data?.visits, data.entries, stopAll);

  // An entry of a visit the page does not know yet (just opened by the
  // backend): reload the patient, so the visit card and the browser have it.
  const knownVisits = patient.data?.visits;
  const { reload: reloadPatient, setData: setPatient } = patient;
  useEffect(() => {
    if (!knownVisits) return;
    const known = new Set(knownVisits.map((v) => v.id));
    const unknown = (x: { visitId: string | null }) => !!x.visitId && !known.has(x.visitId);
    if (data.entries.some(unknown) || xrays.xrays.some(unknown)) void reloadPatient();
  }, [knownVisits, data.entries, xrays.xrays, reloadPatient]);

  const { step, past } = history;
  const dentition = dentitionChoice ?? (view.onlyPrimary ? 'primary' : 'permanent');
  const shown = shownState(step, past, view, data.entries, data.recent);
  const shownVisit = shown.visit;
  const signOff = useVisitSignOff(
    setPatient,
    speech.listening || typing,
    !shown.past && shownVisit?.status === 'OPEN',
    data.reload
  );

  const openManualFor = (q: Clarification) => {
    dictation.dismiss(q.id);
    setManual({ tooth: q.options[0]?.drafts[0]?.tooth ?? null });
  };

  const withBusy = async (e: Entry, action: () => Promise<unknown>) => {
    setBusyId(e.id);
    await action();
    setBusyId(null);
  };

  if (patient.error) {
    return (
      <Banner
        tone="error"
        action={
          <button type="button" className="btn btn--sm" onClick={() => patient.reload()}>
            {t('common.retry')}
          </button>
        }
      >
        {errorMessage(patient.error)}
      </Banner>
    );
  }
  if (!patient.data || data.loading) return <Spinner />;

  return (
    <>
      <PatientHeader patient={patient.data} />
      {/* Phones and portrait tablets: one tap back to the bookings (or the patient list). */}
      <HomeTabs
        className="chart-tabs"
        value="patients"
        onChange={(tab) => {
          setHomeTab(tab);
          navigate({ name: 'patients' });
        }}
      />

      <div className="chart-grid">
        <aside className="chart-grid__info">
          <PatientInfo
            patient={patient.data}
            onSaved={(saved) => setPatient((d) => (d ? { ...d, ...saved } : d))}
          />
        </aside>

        <div className="chart-grid__teeth stack">
          {step && <HistoryBanner step={step} onBack={() => history.show(null)} />}
          <section className="card chart-card">
            <VisitStepper steps={history.timeline} index={history.index} onChange={history.show} />
            <Odontogram
              dentition={dentition}
              chart={shown.chart}
              selected={selected}
              recent={shown.recent}
              highlight={shown.highlight}
              onSelect={setSelected}
            />
            <div className="chart-card__foot">
              <Segmented
                label={t('chart.teeth')}
                value={dentition}
                onChange={setDentition}
                options={[
                  { value: 'permanent', label: t('chart.permanent') },
                  { value: 'primary', label: t('chart.primary') },
                ]}
              />
              <ChartLegend />
            </div>
          </section>
          <XrayCard
            {...xraysShown(xrays.xrays, shown)}
            onAdd={xrays.add}
            onDelete={xrays.remove}
            image={xrays.image}
          />
        </div>

        <div className="chart-grid__side stack">
          <TranscriptCard
            {...visitDictation(
              step,
              dictations.transcripts,
              data.entries,
              shownVisit,
              speech.interim
            )}
            readOnly={!!step}
            onTooth={setSelected}
            onAssign={(transcript, text) =>
              setManual({ tooth: null, from: { clientId: transcript.clientId, text } })
            }
            onEdit={dictations.edit}
          />
          <VisitCard
            // A new visit starts with a fresh card: no half-edited price carried over.
            key={shownVisit?.id ?? 'unsent'}
            visit={shownVisit}
            entries={shown.entries}
            fallbackAuthor={user?.name ?? null}
            busyId={busyId}
            onEdit={setEditing}
            onDelete={(e) => withBusy(e, () => data.reject(e, t('chart.entry.deletedReason')))}
            onPrice={async (v, price) => {
              const saved = await signOff.onPrice(v, price);
              // Confirmed: the spoken price is used up and does not prefill a later visit.
              if (saved) setSpokenPrice(null);
              return saved;
            }}
            onFiscal={signOff.onFiscal}
            onAdd={(v) => setManual({ tooth: null, visitId: v.id })}
            loadLog={(v) => visitsApi.log(v.id)}
            priceRef={shown.past ? undefined : signOff.priceRef}
            spokenPrice={shown.past ? null : spokenPrice}
          />
        </div>
      </div>

      {!step && (
        <DictationPanel
          listening={speech.listening}
          typing={typing}
          supported={speech.supported}
          interim={speech.interim}
          lines={dictation.lines}
          questions={dictation.questions}
          error={speech.error}
          onDevice={speech.onDevice}
          onInstallOnDevice={speech.installOnDevice}
          onSubmitText={dictation.submitText}
          onResolve={dictation.resolve}
          onDismiss={dictation.dismiss}
          onManual={openManualFor}
          onClose={() => {
            setTyping(false);
            speech.clearError();
          }}
        />
      )}

      <ChartFab
        listening={speech.listening}
        supported={speech.supported}
        typing={typing}
        locked={!!step}
        language={dictation.lang}
        onLanguage={() =>
          dictation.changeLanguage(
            LANGUAGES[(LANGUAGES.indexOf(dictation.lang) + 1) % LANGUAGES.length]
          )
        }
        onMic={recording.toggleMic}
        onTyping={recording.toggleTyping}
      />

      <ChartSheets
        selected={selected}
        toothEntries={shown.upTo.filter((e) => e.tooth === selected)}
        readOnly={!!step}
        mode={mode}
        onCloseTooth={() => setSelected(null)}
        manual={manual}
        onCloseManual={() => setManual(null)}
        editing={editing}
        onCloseEditing={() => setEditing(null)}
        busy={busyId !== null && busyId === editing?.id}
        onSave={async (values, from, visitId) => {
          await data.save(
            [{ ...values, transcript: from?.text }],
            'MANUAL',
            from?.clientId,
            visitId ?? null
          );
        }}
        onCorrect={(entry, values) => withBusy(entry, () => data.correct(entry, values))}
      />
    </>
  );
}

/** What the page shows: an earlier visit (read-only chart), or now. */
function shownState(
  step: VisitStep | null,
  past: { chart: ReadonlyMap<number, ToothState>; changed: ReadonlySet<number> } | null,
  view: ReturnType<typeof useChartView>,
  all: Entry[],
  recent: ReadonlySet<number>
) {
  if (step && past) {
    return {
      past: true,
      visit: step.visit,
      entries: step.entries,
      upTo: step.upTo,
      chart: past.chart,
      recent: NO_TEETH,
      highlight: past.changed,
    };
  }
  return {
    past: false,
    visit: view.visit,
    entries: view.current,
    upTo: all,
    chart: view.chart,
    recent,
    highlight: undefined,
  };
}

/**
 * Now: every X-ray of the patient, new ones going to the open visit of the day.
 * An earlier visit: only its own, and new ones are added to it.
 */
function xraysShown(
  all: Xray[],
  shown: { past: boolean; visit: Visit | null }
): { xrays: Xray[]; visitId: string | null } {
  if (!(shown.past && shown.visit)) return { xrays: all, visitId: null };
  const visitId = shown.visit.id;
  return { xrays: all.filter((x) => x.visitId === visitId), visitId };
}

/** Dictations and entries of the visit shown, with what is heard now for the current one. */
function visitDictation(
  step: VisitStep | null,
  transcripts: Transcript[],
  entries: Entry[],
  visit: Visit | null,
  interim: string
): { transcripts: Transcript[]; entries: Entry[]; interim?: string } {
  if (step) {
    return {
      transcripts: transcripts.filter((x) => x.visitId === step.visit.id),
      entries: step.entries,
    };
  }
  // Unsent dictations have no visit yet; they belong to now (as in useChartView).
  const ofNow = (x: { visitId: string | null; createdAt: string; local?: boolean }) =>
    !!x.local || (visit ? x.visitId === visit.id : isToday(x.createdAt));
  return { transcripts: transcripts.filter(ofNow), entries: entries.filter(ofNow), interim };
}

/**
 * A manual entry, optionally for a line of a dictation ("assign to a tooth"),
 * or added to a visit already confirmed (a forgotten procedure).
 */
type ManualEntry = {
  tooth: number | null;
  from?: { clientId: string; text: string };
  visitId?: string;
};

type ChartSheetsProps = {
  selected: number | null;
  toothEntries: Entry[];
  readOnly: boolean;
  mode: EntryKind;
  onCloseTooth: () => void;
  manual: ManualEntry | null;
  onCloseManual: () => void;
  editing: Entry | null;
  onCloseEditing: () => void;
  busy: boolean;
  onSave: (values: EntryValues, from?: ManualEntry['from'], visitId?: string) => Promise<void>;
  onCorrect: (entry: Entry, values: EntryValues) => Promise<void>;
};

/** Tooth details, manual entry and correction sheets. */
function ChartSheets({
  selected,
  toothEntries,
  readOnly,
  mode,
  onCloseTooth,
  manual,
  onCloseManual,
  editing,
  onCloseEditing,
  busy,
  onSave,
  onCorrect,
}: ChartSheetsProps) {
  const { t } = useTranslation();
  return (
    <>
      {selected !== null && (
        <ToothSheet
          tooth={selected}
          entries={toothEntries}
          readOnly={readOnly}
          defaultKind={mode}
          onClose={onCloseTooth}
          onSave={onSave}
        />
      )}

      {manual && (
        <Sheet title={t('chart.manualTitle')} onClose={onCloseManual}>
          {manual.from && (
            <p className="entry__quote" style={{ marginBottom: 12 }}>
              „{manual.from.text}”
            </p>
          )}
          <EntryForm
            initial={{ tooth: manual.tooth }}
            defaultKind={mode}
            submitLabel={t('chart.record')}
            onSubmit={async (values) => {
              await onSave(values, manual.from, manual.visitId);
              onCloseManual();
            }}
          />
        </Sheet>
      )}

      {editing && (
        <Sheet title={t('chart.correctTitle', { tooth: editing.tooth })} onClose={onCloseEditing}>
          {editing.transcript && (
            <p className="entry__quote" style={{ marginBottom: 12 }}>
              „{editing.transcript}”
            </p>
          )}
          <EntryForm
            initial={editing}
            submitLabel={t('chart.correctSubmit')}
            busy={busy}
            onSubmit={async (values) => {
              await onCorrect(editing, values);
              onCloseEditing();
            }}
          />
        </Sheet>
      )}
    </>
  );
}
